import React from "react";
import { Tag, Button, Avatar, DatePicker, Space, Select, Radio, Input } from "antd";
import { order as orderApi, property as propertyApi, app_user as app_userApi, } from "../../apis";
import {
  PageContainer,
  PageHeader,
  PageContent,
} from "../../components/Layout";
import { MyTable } from "../../components";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Filter } from "../../components/Design";
import usePermission from "../../hooks/usePermission";
import moment from "moment";
import styled from "styled-components";
import { dateFormat, renderUser, yen } from "../../utils";
import { useTranslate } from "../../hooks";
import { SearchOutlined } from "@ant-design/icons";
import useFetch from "../../hooks/useFetch";
import { renderStatus } from "../Chat/List";

export default () => {
  const history = useHistory();
  const myTableRef = React.useRef();
  const params = useParams();
  const { search } = useLocation();
  const didInit = React.useRef(false);
  const debounceRef = React.useRef();
  const paramsSearch = React.useMemo(
    () => new URLSearchParams(search),
    [search]
  );
  const { s3host, roles, orderStatuses } = useSelector(
    (state) => state.general
  );
  const { checkPermission } = usePermission();
  const { translate } = useTranslate();
  const [properties] = useFetch(propertyApi.selectAdmin)([]);
  const savedFilters = JSON.parse(sessionStorage.getItem("orderFilters") || "{}");

  const [tablePage, setTablePage] = React.useState(
    parseInt(paramsSearch.get("page") ?? savedFilters.page ?? "1", 10)
  );
  const [tableLimit, setTableLimit] = React.useState(
    parseInt(paramsSearch.get("limit") ?? savedFilters.limit ?? "10", 10)
  );

  const getDate = (paramValue, savedValue) => {
    if (paramValue) return new Date(paramValue);
    if (savedValue) return new Date(savedValue);
    return undefined;
  };

  const [filters, setFilters] = React.useState({
    startDate  : getDate(paramsSearch.get("startDate"), savedFilters.startDate),
    endDate    : getDate(paramsSearch.get("endDate"), savedFilters.endDate),
    query      : paramsSearch.get("query") ?? savedFilters.query ?? "",
    userIdQuery: paramsSearch.get("userIdQuery") ?? savedFilters.userIdQuery ?? "",
    status     : paramsSearch.get("status") ?? savedFilters.status,
    type       : paramsSearch.get("type") ?? savedFilters.type ?? params.type ?? "ADMIN",
    propertyId : paramsSearch.get("propertyId") ?? savedFilters.propertyId ?? params.property,
  });

  const [query, setQuery] = React.useState(filters.query);
  const [userIdQuery, setUserIdQuery] = React.useState(filters.userIdQuery);

  const columns = useHeader({
    s3host,
    roles,
    renderStatus: renderStatus,
    type        : params.type,
    translate,
    checkPermission,
    onAction    : async (key, record) => {
      switch (key) {
        case "detail": {
          history.push(`/chat/${record.conversation._id}`);
          break;
        }
        default:
      }
    },
  });

  React.useEffect(() => {
    if (!didInit.current) return;
    if (filters.query === query && filters.userIdQuery === userIdQuery) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilters((state) => ({ ...state, query, userIdQuery }));
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, userIdQuery]);

  React.useEffect(() => {
    if (!didInit.current) return;
    const newParams = new URLSearchParams();

    if (filters.query) newParams.set("query", filters.query);
    if (filters.userIdQuery) newParams.set("userIdQuery", filters.userIdQuery);
    if (filters.startDate)
      newParams.set("startDate", filters.startDate.toISOString());
    if (filters.endDate)
      newParams.set("endDate", filters.endDate.toISOString());
    if (filters.status) newParams.set("status", filters.status);
    if (filters.type) newParams.set("type", filters.type);
    if (filters.propertyId) newParams.set("propertyId", filters.propertyId);
    newParams.set("page", tablePage);
    newParams.set("limit", tableLimit);

    sessionStorage.setItem("orderFilters", JSON.stringify({ ...filters, page: tablePage, limit: tableLimit }));
    history.replace({ search: newParams.toString() });
  }, [filters, tablePage, tableLimit]);

  // Controlling the page disables MyTable's built-in reset, so reset to page 1
  // ourselves whenever the filters change.
  React.useEffect(() => {
    if (!didInit.current) return;
    setTablePage(1);
  }, [filters]);

  const dateFilter = (e) => {
    setFilters({
      ...filters,
      startDate: e && moment(e[0]).startOf("days").toDate(),
      endDate  : e && moment(e[1]).endOf("days").toDate(),
    });
  };

  const onClear = () => {
    setQuery("");
    setUserIdQuery("");
    setFilters({
      startDate  : undefined,
      endDate    : undefined,
      status     : undefined,
      propertyId : undefined,
      query      : "",
      userIdQuery: "",
      type       : "ADMIN",
    });
  };
  React.useEffect(() => {
    if (!didInit.current) return;
    setFilters({
      ...filters,
      propertyId: params.property,
      type      : params.type ? params.type : "ADMIN",
    });
  }, [params.type, params.property]);

  React.useEffect(() => {
    didInit.current = true;
  }, []);

  return (
    <PageContainer>
      <PageHeader
        title={translate("order.list.pageheader.title.order", "Order")}
      />
      <PageContent>
        <Filter
          extra={[
            <DatePicker.RangePicker
              value={[
                filters.startDate && moment(filters.startDate),
                filters.endDate && moment(filters.endDate),
              ]}
              onChange={dateFilter}
            />,
            <Button onClick={onClear}>
              {translate("system.filter.btn.clear", "Clear")}
            </Button>,
          ]}
        >
          <Radio.Group
            value={filters.type}
            buttonStyle="solid"
            onChange={(e) =>
              setFilters((state) => ({ ...state, type: e.target.value }))
            }
          >
            <Radio.Button value="ADMIN">
              {translate("order.list.radio.btn.admin", "Admin")}
            </Radio.Button>
            <Radio.Button value="CUSTOMER">
              {translate("order.list.radio.btn.customer", "Customer")}
            </Radio.Button>
          </Radio.Group>
          <Input
            className="grid-item"
            prefix={<SearchOutlined />}
            value={userIdQuery}
            placeholder={translate(
              "system.input.search.userId",
              "ユーザー名、ユーザーID"
            )}
            onChange={(e) => setUserIdQuery(e.target.value)}
            allowClear
          />
          <Input
            className="grid-item"
            prefix={<SearchOutlined />}
            value={query}
            placeholder={translate(
              "system.input.search.orderid",
              "Search by order id..."
            )}
            onChange={(e) => setQuery(e.target.value)}
            allowClear
          />
          {filters.type === "ADMIN" && <Select
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: 300 }}
            value={filters.propertyId}
            onChange={(e) => setFilters({ ...filters, propertyId: e })}
            placeholder={translate("income.list.select.property", "Property ")}
            allowClear
            options={properties.map((item) => {
              return {
                value: item._id,
                label: item.name,
              };
            })}
          ></Select>}
          <Select
            className="grid-item"
            value={filters.status}
            style={{ width: "130px" }}
            onChange={(e) => setFilters({ ...filters, status: e })}
            placeholder={translate("order.list.filter.status", "Order status")}
            allowClear
          >
            {orderStatuses.map((type, index) => (
              <Select.Option key={index} value={type}>
                {translate(`system.orderstatus.${type}`, type)}
              </Select.Option>
            ))}
          </Select>
        </Filter>
        <Table
          ref={myTableRef}
          columns={columns}
          filters={filters}
          loadData={orderApi.list}
          rowKey={(record) => record?.rowKey}
          page={tablePage}
          onPageChange={setTablePage}
          limit={tableLimit}
          onLimitChange={setTableLimit}
        />
      </PageContent>
    </PageContainer>
  );
};

const useHeader = ({ onAction, translate, renderStatus }) => {
  return [
    {
      title    : "№",
      dataIndex: "rowKey",
      width    : "20px",
    },
    {
      title : translate("system.useheader.list.property", "Property"),
      render: (row) => {
        return (
          <Space style={{ minWidth: 200 }}>
            <Avatar size={45} shape="square" src={row?.property?.image} />
            <div>
              <b style={{}}>{row?.property?.name}</b> <br />
            </div>
          </Space>
        );
      },
    },
    // {
    //   title : translate("system.useheader.title.owner", "Owner"),
    //   render: (row) => {
    //     return <div style={{ minWidth: 150 }}>{renderUser(row.owner)}</div>;
    //   },
    // },
    {
      title : translate("system.useheader.title.tenant.user", "ユーザー"),
      render: (row) => {
        return <div style={{ minWidth: 150 }}>{renderUser(row.tenant)}</div>;
      },
    },
    {
      title : translate("system.useheader.title.tenant.user", "ユーザーID"),
      render: (row) => {
        return <div>{row.tenant?.userId || "-"}</div>;
      },
    },
    {
      title : translate("system.useheader.title.orderid", "Order ID"),
      render: (row) => {
        return <Tag>{row?.orderId}</Tag>;
      },
    },
    {
      title : translate("system.useheader.title.amount", "Amount"),
      render: (record) => {
        return `${yen(record.amount)}`;
      },
    },
    {
      title : translate("system.useheader.title.tax", "tax"),
      render: (record) => {
        return `${yen(record.tax)}`;
      },
    },
    {
      title : translate("system.useheader.title.total", "Total"),
      render: (record) => {
        return `${yen(record.total)}`;
      },
    },
    {
      title : translate("order.list.useheader.dates", "Dates"),
      render: (record) => {
        return (
          <Tag>{`${dateFormat(record.startDate)}-${dateFormat(
            record.endDate
          )}`}</Tag>
        );
      },
    },
    {
      title : translate("system.useheader.title.status", "Status"),
      render: (record) => {
        return renderStatus(record.orderStatus, translate);
      },
    },
    {
      title : translate("system.useheader.title.date", "Date"),
      render: (record) => {
        return <div>{moment(record.createdAt).format("YYYY/MM/DD HH:mm")}</div>;
      },
    },
    {
      title : translate("system.useheader.title.action", "Action"),
      width : 100,
      render: (record) => {
        return (
          <Button onClick={(key) => onAction("detail", record)}>
            {translate("system.list.action.detail", "Detail")}
          </Button>
        );
      },
    },
  ];
};

const Table = styled(MyTable)`
  .ant-table-cell {
    padding: 8px;
    line-height: 15px;
  }
`;
